import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PatientLayout } from '@/components/layout/PatientLayout';
import { UpcomingAppointments } from '@/components/patient/UpcomingAppointments';
import { DailyHealthLog } from '@/components/patient/DailyHealthLog';
import { patientService, type PatientStats, type MedicationReminder } from '@/services/api/patient.service';
import { 
  Trophy, 
  Flame, 
  Target, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  Calendar,
  Pill
} from 'lucide-react';

export function PatientDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PatientStats | null>(null);
  const [todayReminders, setTodayReminders] = useState<MedicationReminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load stats
      const statsData = await patientService.getStats();
      setStats(statsData);

      // Load today's reminders
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const remindersData = await patientService.getReminders({
        status: 'pending',
        from: today.toISOString(),
        to: tomorrow.toISOString(),
      });
      setTodayReminders(remindersData.reminders);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsTaken = async (reminderId: string) => {
    try {
      await patientService.markReminderAsTaken(reminderId);
      toast.success('Medicamento marcado como tomado!');
      loadData(); // Reload to update stats
    } catch (error) {
      toast.error('Erro ao marcar medicamento');
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <PatientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </PatientLayout>
    );
  }

  return (
    <PatientLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">Bem-vindo de volta!</h2>
          <p className="text-purple-100">
            Continue acompanhando seus medicamentos e mant enha sua saúde em dia!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Points */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pontos</p>
                <p className="text-3xl font-bold text-purple-600">{stats?.points || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Trophy className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Current Streak */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sequência Atual</p>
                <p className="text-3xl font-bold text-orange-600">{stats?.currentStreak || 0}</p>
                <p className="text-xs text-gray-500 mt-1">dias</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Flame className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>

          {/* Adherence Rate */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Taxa de Adesão</p>
                <p className="text-3xl font-bold text-green-600">
                  {stats?.adherenceRate ? `${Math.round(stats.adherenceRate)}%` : '0%'}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Target className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Total Taken */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Medicamentos Tomados</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.taken || 0}</p>
                <p className="text-xs text-gray-500 mt-1">de {stats?.total || 0} total</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Achievement Section */}
        {stats && stats.longestStreak > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                <Trophy className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Melhor Sequência</h3>
                <p className="text-sm text-gray-600">
                  Sua maior sequência foi de <strong>{stats.longestStreak} dias</strong>!
                  {stats.currentStreak === stats.longestStreak && (
                    <span className="ml-2 text-green-600 font-medium">🎉 Recorde atual!</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Today's Reminders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                Lembretes de Hoje
              </h3>
              <button
                onClick={() => navigate('/patient/reminders')}
                className="text-sm text-purple-600 hover:text-purple-800 font-medium"
              >
                Ver todos
              </button>
            </div>
          </div>

          <div className="p-6">
            {todayReminders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum lembrete pendente para hoje</p>
                <p className="text-sm mt-1">Você está em dia! 🎉</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayReminders.map((reminder) => (
                  <div
                    key={reminder.id}
                    className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                        <Pill className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{reminder.medication.name}</h4>
                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                          <span>{reminder.medication.dosageMg}mg</span>
                          <span>•</span>
                          <span>{formatTime(reminder.scheduledFor)}</span>
                        </div>
                        {reminder.instructions && (
                          <p className="text-xs text-gray-500 mt-1">{reminder.instructions}</p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleMarkAsTaken(reminder.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Marcar como Tomado
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Appointments & Health Log */}
          <div className="space-y-6">
            <UpcomingAppointments />
            <DailyHealthLog />
          </div>

          {/* Right Column - Quick Actions */}
          <div className="space-y-4">
            <button
              onClick={() => navigate('/patient/medications')}
              className="w-full bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-purple-300 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Pill className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Meus Medicamentos</h4>
                  <p className="text-sm text-gray-600">Ver lista completa de medicamentos</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/patient/reminders')}
              className="w-full bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Lembretes</h4>
                  <p className="text-sm text-gray-600">Ver todos os lembretes de medicamentos</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/patient/profile')}
              className="w-full bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-green-300 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Meu Progresso</h4>
                  <p className="text-sm text-gray-600">Ver estatísticas detalhadas</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </PatientLayout>
  );
}
